import {clickOnProductBoxSf, galleryClickSortBy} from '@wix/bi-logger-ec-sf/v2';
import {
  viewGallerySf,
  clickToViewPromotionalCardSfSrc10Evid876,
  categoryPageBreadcrumbClicked,
  categoryPageHeroSectionReadLessClicked,
  categoryPageHeroSectionReadMoreClicked,
} from '@wix/bi-logger-stores-data/v2';
import {SiteStore} from '@wix/wixstores-client-storefront-sdk/dist/es/src/viewer-script/site-store/SiteStore';
import {BreadcrumbsItem} from '@wix/wix-ui-tpa';
import {ProductsService} from './ProductsService';
import {FiltersService} from './filters/FiltersService';

export interface GalleryBIServiceDeps {
  siteStore: SiteStore;
  productsService: ProductsService;
  filtersService: FiltersService;
  type: string;
}

export class GalleryBIService {
  private readonly siteStore: SiteStore;
  private readonly productsService: ProductsService;
  private readonly filtersService: FiltersService;
  private readonly type: string;

  constructor(deps: GalleryBIServiceDeps) {
    this.siteStore = deps.siteStore;
    this.productsService = deps.productsService;
    this.filtersService = deps.filtersService;
    this.type = deps.type;
  }

  public getBICollection() {
    const collectionId = this.productsService.getMainCollectionId();
    return collectionId === this.productsService.allProductsCategoryId ? {} : {categoryId: collectionId};
  }

  public getViewCategoriesSfParams(params: {
    isCategoryPage: boolean;
    currentCategory: {media?: {url?: string}; description?: string};
    styles: Record<string, any>;
  }) {
    return {
      isCategoryPage: params.isCategoryPage,
      hasHeroImage: params.currentCategory?.media?.url && params.styles.gallery_showCategoryHeaderImage,
      hasHeroDescription: params.currentCategory?.description && params.styles.gallery_showCategoryHeaderDescription,
      hasProductCounter: params.styles.gallery_showProductsCounter,
      hasBreadcrumbs: params.styles.gallery_showCategoriesBreadcrumbs,
      hasCategoryTree: params.styles.gallery_showCategories,
    };
  }

  public getViewGallerySfParams(params: {
    styles: Record<string, any>;
    commonParams: Record<string, any>;
    commonSettingsParams: Record<string, any>;
    biType: string;
    shouldShowProductOptions: boolean;
    productsLogic: string;
    galleryColumns: number;
    isAutoGrid: boolean;
    isCategoryPage: boolean;
    currentCategory?: {media?: {url?: string}; description?: string};
  }) {
    const {
      styles,
      commonParams,
      commonSettingsParams,
      biType,
      shouldShowProductOptions,
      productsLogic,
      galleryColumns,
      isAutoGrid,
      isCategoryPage,
      currentCategory,
    } = params;

    const {galleryShowFilters, gallery_showAppliedFilters, galleryShowSort, gallery_productDropdownListWidth} = styles;

    let res = {
      ...commonParams,
      componentType: biType,
      filterType:
        (galleryShowFilters &&
          this.filtersService
            ?.getFilterModels()
            .map((m) => m.filterType)
            .join(',')) ||
        '',
      hasAppliedFilters: gallery_showAppliedFilters,
      hasOptions: shouldShowProductOptions,
      hasSorting: galleryShowSort,
      numOfColumns: isAutoGrid ? undefined : galleryColumns,
      productsLogic,
      settings: JSON.stringify({
        ...commonSettingsParams,
        productDropdownListWidth: gallery_productDropdownListWidth,
      }),
    };

    if (isCategoryPage) {
      res = {...res, ...this.getViewCategoriesSfParams({isCategoryPage, currentCategory, styles})};
    }

    return res;
  }

  public sendViewGallerySfReport(viewGallerySfParams: Record<string, any>) {
    void this.siteStore.webBiLogger.report(viewGallerySf(viewGallerySfParams));
  }

  public sendSortClickBiEvent() {
    void this.siteStore.webBiLogger.report(galleryClickSortBy({}));
  }

  public reportProductItemClick(params: {productId: string; index: number; productsLogic: string}) {
    const product = this.productsService.getProduct(params.productId);
    void this.siteStore.webBiLogger.report(
      clickOnProductBoxSf({
        productId: params.productId,
        hasRibbon: !!product.ribbon,
        hasOptions: !!product.options.length,
        index: params.index,
        productType: product.productType,
        galleryType: this.type,
        galleryProductsLogic: params.productsLogic,
      })
    );
    this.productsService.sendClickTrackEvent(product, params.index);
  }

  public reportPromotionItemClick(params: {promoId: string; index: number; productsLogic: string}) {
    void this.siteStore.webBiLogger.report(
      clickToViewPromotionalCardSfSrc10Evid876({
        promo_card_id: params.promoId,
        galleryType: this.type,
        galleryProductsLogic: params.productsLogic,
        index: params.index,
        ...this.getBICollection(),
      })
    );
  }

  public handleCategoryBreadcrumbsClick(params: {item: BreadcrumbsItem; originCategoryId: string}) {
    return this.siteStore.webBiLogger.report(
      categoryPageBreadcrumbClicked({
        link: params.item.href,
        originCategoryId: params.originCategoryId,
        destinationCategoryId: params.item.id.toString(),
      })
    );
  }

  /* istanbul ignore next: cannot be easily tested since activating it relies on jsdom having element dimensions */
  public handleCategoryClampClick(isClamped: boolean) {
    return isClamped
      ? this.siteStore.webBiLogger.report(categoryPageHeroSectionReadLessClicked({}))
      : this.siteStore.webBiLogger.report(categoryPageHeroSectionReadMoreClicked({}));
  }
}
